import { ArrowUpRight } from "./SitePrimitives";
import { LanguageSwitcher } from "../LanguageSwitcher";
import type { SupportedLocale } from "../../lib/localization";

type NavItem = { label: string; href: string };

export const SITE_LOGO = "/site/logo.svg";

export function SiteNav({
  logo,
  items,
  ctaLabel,
  ctaHref,
  showCta,
  menuOpen,
  onToggleMenu,
  onNavigate,
}: {
  logo?: string;
  items: NavItem[];
  ctaLabel: string;
  ctaHref: string;
  showCta: boolean;
  locale: SupportedLocale;
  onLocaleChange?: (locale: SupportedLocale) => void;
  menuOpen: boolean;
  onToggleMenu: () => void;
  onNavigate: (href: string) => void;
}) {
  const go = (href: string) => (event: React.MouseEvent) => {
    if (href.startsWith("#")) {
      event.preventDefault();
      onNavigate(href);
    }
  };
  return (
    <header className="pointer-events-none fixed inset-x-0 top-4 z-50 flex justify-center px-4 min-[810px]:top-6">
      <div className="pointer-events-auto relative flex h-[53px] w-full max-w-[920px] items-center justify-between rounded-2xl bg-[#0F2827] pl-4 pr-2 min-[810px]:h-[57px] min-[810px]:rounded-[20px]">
        <a href="#hero" className="flex shrink-0 items-center" onClick={go("#hero")} aria-label="ON45D Studio">
          <img src={logo || SITE_LOGO} alt="ON45D Studio" className="h-[33px] w-auto min-[810px]:h-[38px]" />
        </a>
        <nav
          className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-6 min-[810px]:flex"
          aria-label="Primary"
        >
          {items.map((item) => (
            <a
              key={item.href + item.label}
              href={item.href}
              className="text-[16px] font-semibold leading-[1.2] tracking-[0.02em] text-[var(--public-muted)] transition-colors hover:text-[var(--public-paper)]"
              onClick={go(item.href)}
            >
              {item.label}
            </a>
          ))}
        </nav>
        {showCta ? (
          <a href={ctaHref} className="site-btn site-btn-sm site-cta-45 hidden min-[810px]:inline-flex" onClick={go(ctaHref)}>
            <span className="site-cta-label">{ctaLabel}</span>
            <ArrowUpRight className="site-cta-arrow h-[11px] w-[11px]" />
          </a>
        ) : null}
        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center min-[810px]:hidden"
          aria-expanded={menuOpen}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={onToggleMenu}
        >
          <span className="relative block h-[14px] w-[18px]">
            <span
              className={`absolute left-0 h-[2px] w-full rounded bg-[var(--public-accent)] transition-transform ${
                menuOpen ? "top-[6px] rotate-45" : "top-0"
              }`}
            />
            <span
              className={`absolute left-0 top-[6px] h-[2px] w-full rounded bg-[var(--public-accent)] transition-opacity ${
                menuOpen ? "opacity-0" : ""
              }`}
            />
            <span
              className={`absolute left-0 h-[2px] w-full rounded bg-[var(--public-accent)] transition-transform ${
                menuOpen ? "top-[6px] -rotate-45" : "top-[12px]"
              }`}
            />
          </span>
        </button>
      </div>
    </header>
  );
}

export function SiteMobileMenu({
  items,
  ctaLabel,
  ctaHref,
  showCta,
  locale,
  onLocaleChange,
  onNavigate,
}: {
  items: NavItem[];
  ctaLabel: string;
  ctaHref: string;
  showCta: boolean;
  locale: SupportedLocale;
  onLocaleChange?: (locale: SupportedLocale) => void;
  onNavigate: (href: string) => void;
}) {
  return (
    <div className="fixed inset-x-4 top-[77px] z-[60] rounded-2xl bg-[#0F2827] p-6 min-[810px]:hidden">
      <nav className="flex flex-col gap-5" aria-label="Mobile">
        {items.map((item) => (
          <a
            key={item.href + item.label}
            href={item.href}
            className="text-[18px] font-semibold text-[var(--public-muted)]"
            onClick={(event) => {
              if (item.href.startsWith("#")) {
                event.preventDefault();
                onNavigate(item.href);
              }
            }}
          >
            {item.label}
          </a>
        ))}
      </nav>
      <div className="mt-6 flex items-center justify-between gap-4">
        {onLocaleChange ? <LanguageSwitcher locale={locale} onChange={onLocaleChange} /> : <span />}
        {showCta ? (
          <a
            href={ctaHref}
            className="site-btn site-btn-sm"
            onClick={(event) => {
              if (ctaHref.startsWith("#")) {
                event.preventDefault();
                onNavigate(ctaHref);
              }
            }}
          >
            {ctaLabel}
          </a>
        ) : null}
      </div>
    </div>
  );
}
